import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Formik } from 'formik'; // useFormikContext
import { Form, Select, DatePicker, Checkbox, AutoComplete } from 'formik-antd';
import {
  Modal,
  Spin,
  Divider,
  Input as InputAntd,
  Select as SelectAntd,
  Space,
  Tooltip,
  message,
  Button as ButtonAntd,
  Typography,
} from 'antd';
import { PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { getValidationSchema } from './projectBidSchema';
import Row from '~/components/Row';
import FormControl from '~/components/Form/FormControl';
import errorHandler from 'Utils/errorHandler';
import Button from '~/components/Button';
import { Footer, TabProject } from './styles';
import Box from '~/components/Box';
import { projectStatus } from '~/services/lists/projectStatus';
import { findAllCountries } from '~/services/hooks/countries';
import { createProject, updateProject } from '~/services/hooks/projects';
import { format } from 'date-fns';
import { uploadFiles } from '~/services/hooks/files';
import { createHotel } from '~/services/hooks/hotels';
import { customerCreateQb, getCustomerQb } from '~/services/hooks/quickbooksApi';
import debounce from 'lodash/debounce';
import { buildProjectBidInitialValues } from './projectBidInitialValues';
import PropertyTab from './tabs/PropertyTab';
import OwnershipTab from './tabs/OwnershipTab';
import ArchitectTab from './tabs/ArchitectTab';
import DesignerTab from './tabs/DesignerTab';

export default function ProjectBidForm({ visible, onClose, record, projectTypes, hotels, setHotels, customer }) {

  const projectData = buildProjectBidInitialValues(record, customer);
  const { t } = useTranslation();
  const { Option } = Select;
  const { Text } = Typography;
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const uploadContract = [];
  const uploadInsurance = [];
  const [selectedTab, setSelectedTab] = useState('1');
  const inputRef = useRef(null);
  const [hotelName, setHotelName] = useState();
  const [qbOptions, setQbOptions] = useState([]);
  const [loadingQb, setLoadingQb] = useState(false);
  const [selectedQb, setSelectedQb] = useState(null);

  const handleQbSearch = async value => {
    if (!value) {
      setQbOptions([]);
      return;
    }
    setLoadingQb(true);
    try {
      const resp = await getCustomerQb({ name: encodeURIComponent(value), job: true });
      const customers = resp?.QueryResponse?.Customer || [];
      const options = customers.map(c => ({
        label: c.FullyQualifiedName,
        value: c.Id,
        data: c,
      }));
      setQbOptions(options);
    } catch (err) {
      console.error(err);
    }
    setLoadingQb(false);
  };

  const debouncedQbSearch = useCallback(debounce(handleQbSearch, 500), [handleQbSearch]);

  const onHotelNameChange = event => {
    setHotelName(event.target.value);
  };

  const handleAddHotel = async setFieldValue => {
    try {
      // Check if the hotel already exists
      const hotelExists = hotels.find(hotel => hotel.name === hotelName);
      if (hotelExists) {
        message.warning('Hotel already exists');
        return;
      }

      // Create the new hotel in the database
      const data = await createHotel({
        id: null,
        name: hotelName,
        customer: customer,
      });

      // Update the hotels list and reset the name
      setHotels([...hotels, data]);
      setHotelName('');

      // Update the hotelId field in Formik with the new hotel's ID
      setFieldValue('hotelId', data.id);
    } catch (error) {
      console.error('Error creating hotel:', error);
      message.error('Failed to create hotel. Please try again.');
    }
  };

  const fetchCountries = async () => {
    setLoading(true);
    const data = await findAllCountries();
    setCountries(data);
    setLoading(false);
  };

  const handleSave = async (values, { setErrors }) => {
    setLoading(true);
    if (values.basic) {
      values.flag = 'PROJECT';
    }

    try {
      // Only tab 5 uploads files and creates/updates
      if (selectedTab === '5') {
        let qbIdToUse;
        if (record?.qbId) {
          qbIdToUse = record.qbId;
        } else if (selectedQb?.Id) {
          qbIdToUse = selectedQb.Id;
        } else {
          const customerDTO = {
            PrimaryEmailAddr: { Address: values.ownership.representativeEmail },
            DisplayName: values.description,
            Job: true,
          };
          const created = await customerCreateQb(customerDTO);
          qbIdToUse = created.Id;
          message.success(t('messages:customerSuccess'));
        }
        values.qbId = qbIdToUse;

        values.startDate = format(new Date(values.startDate), "yyyy-MM-dd'T'HH:mm:ss");
        values.endDate = format(new Date(values.endDate), "yyyy-MM-dd'T'HH:mm:ss");
        values.actualEndDate = format(new Date(values.actualEndDate), "yyyy-MM-dd'T'HH:mm:ss");
        values.deadlineDate = values.deadlineDate
          ? format(new Date(values.deadlineDate), "yyyy-MM-dd'T'HH:mm:ss")
          : null;
        values.insuranceExpirationDate = format(new Date(values.insuranceExpirationDate), "yyyy-MM-dd'T'HH:mm:ss");

        // contract upload
        if (uploadContract.length) {
          const [first] = await uploadFiles(uploadContract);
          values.contract = first;
        } else {
          values.contract = null;
        }

        // insurance upload
        if (uploadInsurance.length) {
          const [first] = await uploadFiles(uploadInsurance);
          values.tenantInsurance = first;
        } else {
          values.tenantInsurance = null;
        }

        // update ou create
        if (values.id != null) {
          await updateProject(values);
        } else {
          await createProject(values);
        }

        onClose();
      }
    } catch (error) {
      setErrors(errorHandler(error));
    } finally {
      setLoading(false);
      onChangeTab((parseInt(selectedTab, 10) + 1).toString());
    }
  };

  const onChangeTab = key => {
    setSelectedTab(key);
  };

  useEffect(() => {
    setSelectedTab('1');
    if (visible) {
      fetchCountries();
    }
  }, [visible]);

  // function FormikLogger() {
  //   const { values } = useFormikContext();
  //   // useEffect(() => {
  //   //   console.log('⚙️ Formik values:', values);
  //   // }, [values]);
  //   return "ok"; // don't render anything
  // }

  return (
    <Formik
      initialValues={projectData} // record || initialValues
      enableReinitialize
      onSubmit={handleSave}
      validationSchema={() => Yup.lazy(values => getValidationSchema(values, selectedTab))}
      validateOnBlur={true}
      validateOnChange={false}
    >
      {formik => {
        const { errors, isSubmitting, submitForm, resetForm, touched, values, setFieldValue } = formik;
        return (
          <>
            <Modal
              width="620px"
              title={values.id !== null ? 'Edit' : 'New'}
              onCancel={onClose}
              afterClose={resetForm}
              open={visible}
              loading={loading || isSubmitting}
              footer={
                <Footer>
                  <Space>
                    <Button loading={loading || isSubmitting} color={'default'} onClick={onClose}>
                      {'Cancel'}
                    </Button>
                    <Button loading={loading || isSubmitting} color={'primary'} onClick={submitForm}>
                      {selectedTab === '5' ? 'Save' : 'Continue'}
                    </Button>
                  </Space>
                </Footer>
              }
            >
              <Spin spinning={loading || isSubmitting}>
                <Form>
                  {/* <FormikLogger /> */}
                  <TabProject
                    type="card"
                    size={'small'}
                    activeKey={selectedTab}
                    onChange={onChangeTab}
                    items={[
                      // BID/PROJECT DATA
                      {
                        key: '1',
                        label: t('screens:bids.data.bid'),
                        disabled: selectedTab !== '1',
                        children: (
                          <Box>
                            <Row>
                              {/* BASIC - is a checkbox to change to change basic columm in data base */}
                              <FormControl
                                cols={{ xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }}
                                field="basic"
                                label={t('screens:basics.data.basic')}
                                error={(touched.basic && errors.basic) || errors.basic}
                              >
                                <div style={{ paddingTop: 0 }}>
                                  <Checkbox value="basic" name="basic" />
                                  <span style={{ marginLeft: 10 }}>{t('screens:basics.data.is_basic')}</span>
                                </div>
                              </FormControl>
                              {/* HOTELS */}
                              <FormControl
                                cols={{ xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }}
                                field="hotelId"
                                label={t('screens:projects.hotel')}
                                required
                                error={(touched.hotelId && errors.hotelId) || errors.hotelId}
                              >
                                <SelectAntd
                                  key={hotels.length}
                                  name="hotelId"
                                  placeholder="Select hotel"
                                  value={values.hotelId}
                                  dropdownRender={menu => (
                                    <>
                                      {menu}
                                      <Divider
                                        style={{
                                          margin: '8px 0',
                                        }}
                                      />
                                      <Space
                                        style={{
                                          padding: '0 8px 4px',
                                        }}
                                      >
                                        <InputAntd
                                          placeholder="Enter new hotel"
                                          ref={inputRef}
                                          value={hotelName}
                                          onChange={e => {
                                            onHotelNameChange(e);
                                          }}
                                        />
                                        <ButtonAntd
                                          type="text"
                                          icon={<PlusOutlined />}
                                          onClick={() => {
                                            if (hotelName && hotelName.length < 255) {
                                              handleAddHotel(setFieldValue);
                                            } else {
                                              message.warning(
                                                'Hotel name cannot be empty and must be less than 255 characters'
                                              );
                                            }
                                          }}
                                        >
                                          Add hotel
                                        </ButtonAntd>
                                        <Tooltip title="Warning: A hotel will be inserted for the customer, even if you do not complete this operation.">
                                          <InfoCircleOutlined />
                                        </Tooltip>
                                      </Space>
                                    </>
                                  )}
                                  options={hotels?.map(a => ({
                                    label: a.name,
                                    value: a.id,
                                  }))}
                                  onChange={value => {
                                    setFieldValue('hotelId', value);
                                  }}
                                />
                              </FormControl>
                              {/* DESCRIPTION */}
                              <FormControl
                                cols={{ xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }}
                                error={(touched.description && errors.description) || errors.description}
                                field="description"
                                label={t('screens:bids.data.description')}
                                required
                              >
                                <AutoComplete
                                  name="description"
                                  style={{ width: '100%' }}
                                  loading={loadingQb}
                                  options={qbOptions}
                                  onSearch={debouncedQbSearch}
                                  onSelect={(value, option) => {
                                    setSelectedQb(option.data);
                                    setFieldValue('description', option.label);
                                  }}
                                  onChange={val => {
                                    // se o usuário digitar algo que não esteja na lista
                                    setSelectedQb(null);
                                    setFieldValue('description', val);
                                  }}
                                  notFoundContent={loadingQb ? <Spin size="small" /> : null}
                                  placeholder={t('screens:bids.data.descriptionSearchPlaceholder')}
                                  value={values.description}
                                />
                              </FormControl>
                              {/* STATUS */}
                              <FormControl
                                cols={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}
                                field="type"
                                label={t('screens:bids.status')}
                                required
                                error={(touched.status && errors.status) || errors.status}
                              >
                                <Select showSearch name="status" label={t('screens:bids.data.status')}>
                                  {projectStatus.map(item => {
                                    return (
                                      <Option key={item.id} value={item.code}>
                                        {item.description}
                                      </Option>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                              {/* TYPE */}
                              <FormControl
                                cols={{ xs: 15, sm: 14, md: 14, lg: 14, xl: 14 }}
                                field="type"
                                label={t('screens:bids.data.type')}
                                required
                                error={(touched.type && errors.type) || errors.type}
                              >
                                <Select showSearch name="type" label={t('screens:bids.data.type')}>
                                  {projectTypes?.map(item => {
                                    return (
                                      <Option key={item.name} value={item.name}>
                                        {item.description}
                                      </Option>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Row>
                            <Divider orientation="left" orientationMargin="0">
                              <Text>{t('screens:bids.data.schedule')}</Text>
                            </Divider>
                            <Row>
                              <FormControl
                                cols={{ xs: 8, sm: 7, md: 7, lg: 7, xl: 7 }}
                                field="deadlineDate"
                                label={t('screens:bids.data.deadlineDate')}
                              >
                                <DatePicker name="deadlineDate" format="MM/DD/Y" placeholder={t('messages:select')} />
                              </FormControl>
                            </Row>
                          </Box>
                        ),
                      },
                      // PROPERTY DATA
                      {
                        key: '2',
                        label: t('screens:bids.data.property'),
                        children: <PropertyTab {...formik} t={t} countries={countries} />,
                        disabled: selectedTab !== '2',
                      },
                      // OWNERSHIP DATA
                      {
                        key: '3',
                        label: t('screens:bids.data.ownership'),
                        children: <OwnershipTab {...formik} t={t} countries={countries} />,
                        disabled: selectedTab !== '3',
                      },
                      // ARCHITECT DATA
                      {
                        key: '4',
                        label: t('screens:bids.data.architect'),
                        disabled: selectedTab !== '4',
                        children: <ArchitectTab {...formik} t={t} countries={countries} />,
                      },
                      // DESIGNER DATA
                      {
                        key: '5',
                        label: t('screens:bids.data.designer'),
                        disabled: selectedTab !== '5',
                        children: <DesignerTab {...formik} t={t} countries={countries} />,
                      },
                    ]}
                  />
                </Form>
              </Spin>
            </Modal>
          </>
        );
      }}
    </Formik>
  );
}
